import { Enum, Hash, Num, Obj, Str } from '@livecontrol/core-utils';
import type { Phone } from '@livecontrol/locale-us';
import base64 from 'base-64';
import type { IANAZone } from 'luxon';
import urlencode from 'urlencode';
import type { Asset } from './asset';
import { Event } from './event';
import type { User } from './user';

export interface Account {
  id: Account.ID;
  name: string;
  address?: string;
  phone?: Phone;
  timezone: IANAZone;
  avatar: string;
  leadTime: number;
  industry?: Account.Industry;
  // subUsers: Account.SubUser[];
  accountOwnerName: string;
  organizationPhoneNumber?: Phone;
  accountOwnerEmail: string;
  preferredContactMethod?: string;
  contactRole: User.ContactRole[];
  userAccountDetails: {
    accountType: {
      id: string;
      name: string;
    };
  };
  userGlobalSettings: userGlobalSettings;
}

export interface userGlobalSettings {
  clientTier?: string;
  analyticsRange?: number;
  clientContactInfo?: boolean;
  cueSheet?: boolean;
  eventScheduling?: boolean;
  onSiteEventContact?: boolean;
  operatorEventNotes?: boolean;
  passwordProtect?: boolean;
  recurringEvents?: boolean;
  webplayerRedirect?: boolean;
  webplayerRegistration?: boolean;
  webplayerRegistrationRequired?: boolean;
  destinationsEnabled?: boolean;
  webplayerShowViewers?: boolean;
  webplayerGuestbook?: boolean;
  trimming?: boolean;
}

export namespace Account {
  export type ID = number;
  export type Like = Account | ID | string;

  export const toId = (account?: Like): ID | undefined =>
    Num.normalize(Obj.isObject<Account>(account) ? account.id : account, { positive: true });

  export enum Industry {
    House_Of_Worship = 1,
    Event_Venue = 2,
    Comedy = 3,
    Fitness = 4,
    Conference = 5,
    Content_Creator = 6,
    Funeral = 7,
    Government = 8,
    Education = 9,
    Internal = 10,
    Church = 11,
    Synagogue = 12,
    Other = 13
  }

  export namespace Industry {
    export const normalize = (value: unknown): Industry | undefined =>
      Enum.normalize(Industry, value);
  }

  export interface Billing {
    stripe: {
      url?: string;
      token?: string;
      defaultLast4CardNumber?: string;
      defaultCardBrand?: string;
      expMonth?: string;
      expYear?: string;
    };
    credits: {
      total: number;
      price: number;
      id?: number;
      first_credit_time_limit: number;
      over_time_limit_cost: number;
      recurrence: string;
      recurrence_amount: number;
      credit_balances: {
        amount: number;
        credit_type: string;
      }[] | undefined;
    };
    client_booking: {
      cb2_price: number;
      cb3_price: number;
      cb2_host_price: number;
      cb3_host_price: number;
      client_pays_enabled: boolean;
      pay_now_enabled: boolean;
      pay_later_enabled: boolean;
    };
  }

  export interface Purchase {
    stripeInvoiceId?: string;
    paid: boolean;
    amount: number;
  }

  export interface Invoice {
    id: number;
    clientId?: number;
    status?: string;
    type?: string;
    datePaid?: string;
    stripeInvoiceId: string;
    oneTimeCreditAmount?: number;
  }

  export namespace Invoice {
    /* eslint-disable @typescript-eslint/no-shadow */
    export type Like = Invoice | Purchase | string;

    export const toId = (invoice: Like): string | undefined =>
      Str.normalize(Obj.isObject<Invoice | Purchase>(invoice) ? invoice.stripeInvoiceId : invoice);
    /* eslint-enable @typescript-eslint/no-shadow */
  }

  export type SubUser = Omit<User, 'role' | 'status'> & {
    permissions: SubUser.Permissions;
    hasAllLocations: boolean;
    contactRole?: SubUser.ContactRole[];
    sub_user_locations?: SubUser.LocationPermissions[];
  };

  export namespace SubUser {
    export type Permissions = User.Permissions & {
      id: number;
    };
    export type ContactRole = User.ContactRole;
    export type LocationPermissions = User.LocationPermissions;
  }

  export type Features = Set<string>;

  export const FeatureKeys = {
    ASSET_ANALYTICS: 'asset:analytics'
  };

  export interface Contact {
    id: string;
    name: string;
    email: string;
    phone: string;
  }

  export interface WebPlayer {
    endpoint?: string;
    displayName?: string;
    about?: string;
    theme?: string;
    logo?: string;
    background?: string;
    donation?: WebPlayer.Donation;
    customUrl?: string;
    tabs?: WebPlayer.Tabs;
    followUsLinks?: WebPlayer.FollowUsLinks;
    intro?: string;
    outro?: string;
    default_intro?: string;
    default_outro?: string;
    introDefaultSlide?: WebPlayer.Slide
    outroDefaultSlide?: WebPlayer.Slide
    isCustomButtonEnabled?: boolean;
    customButton?: WebPlayer.CustomButton;
    custom_donate_button_copy?: string;
    donate_link?: string;
  }

  export namespace WebPlayer {
    export interface CustomButton {
      title?: string;
      url?: string;
    }

    export interface Donation {
      label?: string;
      link?: string;
    }

    export interface Tabs {
      archive?: boolean;
      schedule?: boolean;
      chat?: boolean;
      profile?: boolean;
      reminders?: boolean;
    }

    export interface FollowUsLinks {
      facebook?: string;
      instagram?: string;
      website?: string;
      youtube?: string;
    }

    export interface Slide {
      slide_type: string;
      file_name: string;
      file_type: string;
      url: string;
      duration?: number;
      size?: number;
      position_index?: number;
    }

    export const Constraints = {
      ABOUT_MAX_LENGTH: 500
    };

    // Clients with a custom domain must be linked through it instead of the slug based URI
    export const makeBaseURL = ({
      uri,
      slug,
      customUrl
    }: {
      uri: string;
      slug: string;
      customUrl?: string;
    }): string => {
      const custom = customUrl?.trim().replace(/\/+$/, '');

      if (!custom) {
        return uri.replace('{slug}', slug);
      }

      return /^https?:\/\//i.test(custom) ? custom : `https://${custom}`;
    };

    export const makeURL = (args: {
      uri: string;
      slug: string;
      customUrl?: string;
      event?: Event.Like;
      password?: string;
      muxAssetId?: string;
      clip?: boolean;
    }): string => {
      const { uri, slug, customUrl, ...rest } = args;
      const event = Event.toId(rest.event);
      const password = rest.password ? urlencode(base64.encode(rest.password)) : undefined;

      return [
        makeBaseURL({ uri, slug, customUrl }),
        rest.clip && rest.muxAssetId ? `/${rest.muxAssetId}` : event ? `/${Hash.digest(event.toString())}` : undefined,
        password ? `?pwd=${password}` : undefined
      ]
        .filter((µ) => !!µ)
        .join('');
    };

    export const makeEventsURL = (args: {
      uri: string;
      slug: string;
      customUrl?: string;
      events?: Event.Like[];
      password?: string;
    }): string => {
      if (args.events?.length === 1) {
        return makeURL({...args, event: args.events[0]});
      }

      const { uri, slug, customUrl, ...rest } = args;

      const events = (rest.events ?? [])
      .map(Event.toId)
      .filter(µ => !!µ)
      .map(id => Hash.digest(id!.toString()));

      const password = rest.password ? urlencode(base64.encode(rest.password)) : undefined;

      return [
        makeBaseURL({ uri, slug, customUrl }),
        `/${events.join('&')}`,
        password ? `?${events.map((_, i) => `pwd${i + 1}=${password}`).join('&')}` : undefined
      ]
        .filter((µ) => !!µ)
        .join('');
    };
  
    export const makeWebplayerUrl = (args: {
      uri: string;
      slug: string;
      event?: Event.Like;
      password?: string;
      customUrl?: string;
    }): string => {
      const { uri, slug, customUrl, ...rest } = args;
      const event = Event.toId(rest.event);
      const password = rest.password ? urlencode(base64.encode(rest.password)) : undefined;

      return [
        makeBaseURL({ uri, slug, customUrl }),
        event ? `/${Hash.digest(event.toString())}?` : undefined,
        password ? `&pwd=${password}` : undefined
      ]
        .filter((µ) => !!µ)
        .join('');
    };

    export const makeEventSlug = (args: { eventId: number | undefined }): string => {
      const { eventId } = args;

      if (eventId) {
        return `${Hash.digest(eventId.toString())}`;
      }

      return '';
    };

    export const makeAssetURL = ({
      venueURI,
      slug,
      customUrl,
      asset
    }: {
      venueURI: string;
      slug: string;
      customUrl?: string;
      asset: Asset;
    }): string => {
      if (asset.event) {
        return makeURL({ uri: venueURI, slug, customUrl, event: asset.event });
      }

      const muxAssetID = asset.mux?.assetId;

      if (!muxAssetID) {
        return '';
      }

      return `${makeBaseURL({ uri: venueURI, slug, customUrl })}/${Hash.digest(muxAssetID)}`;
    };

    export const makeGuestbookUrl = (args: {
      uri: string;
      slug: string;
      customUrl?: string;
      event?: Event.Like;
    }): string => {
      const { uri, slug, customUrl, ...rest } = args;
      const event = Event.toId(rest.event);

      return [
        makeBaseURL({ uri, slug, customUrl }),
        '/guest-book',
        event ? `/${Hash.digest(event.toString())}` : undefined
      ]
        .filter((µ) => !!µ)
        .join('');
    };
  }

  export namespace ClientBooking {
    export const makeClientBookingURL = ({
      uri,
      slug,
      eventId
    }: {
      uri: string;
      slug: string;
      eventId: number;
    }): string => `${uri.replace('{slug}', slug)}/${eventId}`;
  }

  export interface S3 {
    url?: string;
    bucket?: string;
    key?: string;
  }

  export interface SlugCheck {
    slug_valid: boolean;
  }

  export namespace Plaid {
    export type Token = string;
    export type Account = string;
  }

  // Force Typescript to export this as both a type and value.
  export const __unused = false;
}
